import { FunctionComponent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import Typography from "@mui/material/Typography";
import { gql, useQuery } from "@apollo/client";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import HeaderPublic from "../Layout/Header/HeaderPublic";
import Loading from "../Loading/Loading";
import { JesprUser } from "../User/User";
import { JesprBike, JesprBikeCategory } from "../activities/Activity/Bike";
import Map from "../activities/Activity/Map";
import Charts from "./Charts";
import PublicApolloClient from "./publicClient";

export interface JesprPublicActivity {
  id: string;
  name: string;
  active: boolean;
  laps: JesprLap[];
  dataPoints: JesprDataPoint[];
  dataPointsPolyline: string;
  start: Date;
  distance: number;
  time: number;
  elevationGain: number;
  platformSyncs: JesprPlatformSync[];
  user: JesprUser;
  bike?: JesprBike;
}

export interface JesprLap {
  index: number;
  finalTime: number;
  finalDistance: number;
  finalElevationGain: number;
}

interface JesprPlatformSync {
  platformId: string;
  externalId: string;
}

export interface JesprDataPoint {
  lap: number;
  timestamp: string;
  coordinates: number[];
  elevation: number;
  temperature: number;
  gradient: number;
  speed?: number;
  power: number;
  cadence: number;
  heartRate: number;
  torqueEfficiency: {
    left: number;
    right: number;
  };
  pedalBalance: {
    left: number;
    right: number;
  };
  pedalSmoothness: {
    left: number;
    right: number;
  };
}

interface PublicActivityData {
  activity: JesprPublicActivity;
}

interface PublicActivityVars {
  id: string;
  sharingToken: string;
}

const GET_ACTIVITY = gql`
  query GetPublicActivity($id: ID!, $sharingToken: String!) {
    activity(id: $id, sharingToken: $sharingToken) {
      id
      name
      active
      dataPoints {
        id
        cadence
        coordinates
        elevation
        gradient
        heartRate
        power
        speed
        temperature
        timestamp
      }
    }
  }
`;

let clientHeight = 0;
if (document.documentElement) {
  clientHeight = document.documentElement.clientHeight;
}
const windowHeight = Math.max(clientHeight, window.innerHeight || 0);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mapContainer: {
      height: windowHeight - 200,
      marginBottom: theme.spacing(5),
    },
    deleteItem: {
      color: "red",
    },
    buttonContainer: {
      display: "flex",
      justifyContent: "flex-end",
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    backButton: {
      position: "fixed",
      bottom: theme.spacing(4),
      left: theme.spacing(4),
      zIndex: 3,
    },
  }),
);

const PublicActivity: FunctionComponent = () => {
  const classes = useStyles();
  const { activityID } = useParams();
  const [searchParams] = useSearchParams();
  const sharingToken = searchParams.get("sharingToken");

  const { loading, error, data } = useQuery<
    PublicActivityData,
    PublicActivityVars
  >(GET_ACTIVITY, {
    client: PublicApolloClient,
    variables: { id: activityID || "", sharingToken: sharingToken || "" },
    pollInterval: 5000,
  });

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <ErrorMessage error={error} />;
  }
  if (!data || !data.activity) {
    throw Error("missing data");
  }

  const activityName = data.activity.name;
  const isStarted = data.activity.dataPoints.length > 0;

  const isIndoor = Boolean(
    data.activity.bike &&
    data.activity.bike.category === JesprBikeCategory.Indoor,
  );

  return (
    <>
      <HeaderPublic />
      <div className="jespr-main-content">
        <Grid container justifyContent="space-between">
          <Grid item sm={6}>
            <Typography variant="h5" paragraph>
              {activityName}
            </Typography>
          </Grid>
        </Grid>

        {isStarted ? (
          <>
            <div className={classes.mapContainer}>
              <Map activity={data.activity} />
            </div>

            <Charts activity={data.activity} isIndoor={isIndoor} />
          </>
        ) : (
          <p>The requested activity has not been started yet.</p>
        )}
      </div>
    </>
  );
};

export default PublicActivity;
